import { useEffect, useRef, useState } from 'react';
import { AppIcon } from './AppIcon.tsx';
import { texts } from '../texts/de.ts';

/** Eigenschaften des Timers. */
export interface TimerProps {
  /** Dauer in Minuten. */
  readonly minutes: number;
  /** Kurze Beschriftung, zum Beispiel der Schrittitel. */
  readonly label: string;
}

/** Formatiert Sekunden als mm:ss. */
function format(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

/**
 * Timer für einen Arbeitsschritt.
 *
 * Läuft unabhängig weiter, während man scrollt oder den Schritt wechselt.
 * Ist die Zeit um, ertönt ein kurzer Ton und die Anzeige hebt sich hervor.
 */
export function Timer({ minutes, label }: TimerProps) {
  const total = Math.max(1, Math.round(minutes * 60));
  const [remaining, setRemaining] = useState(total);
  const [running, setRunning] = useState(false);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!running) {
      return;
    }
    const id = window.setInterval(() => {
      setRemaining((value) => {
        if (value <= 1) {
          window.clearInterval(id);
          setRunning(false);
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [running]);

  // Signalton, sobald die Zeit abgelaufen ist.
  useEffect(() => {
    if (remaining === 0 && !doneRef.current) {
      doneRef.current = true;
      playChime();
    }
  }, [remaining]);

  const finished = remaining === 0;

  return (
    <div
      className={`mt-3 rounded-2xl border p-3 ${
        finished ? 'border-accent bg-accent-soft' : 'border-border-subtle bg-surface-sunken'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs text-content-muted">{label}</p>
          <p
            className={`font-mono text-3xl font-bold tabular-nums ${finished ? 'text-accent-strong' : ''}`}
            aria-live="polite"
          >
            {format(remaining)}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setRunning((value) => !value)}
            disabled={finished}
            className="flex items-center justify-center gap-2 rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-50"
          >
            <AppIcon name="clock" size={16} />
            {running ? texts.cook.timerPause : texts.cook.timerStart}
          </button>
          <button
            type="button"
            onClick={() => {
              setRunning(false);
              setRemaining(total);
              doneRef.current = false;
            }}
            className="rounded-xl border border-border-subtle px-4 py-2 text-xs"
          >
            {texts.cook.timerReset}
          </button>
        </div>
      </div>
      {finished ? (
        <p className="mt-2 text-sm font-semibold text-accent-strong">{texts.cook.timerDone}</p>
      ) : null}
    </div>
  );
}

/** Kurzer Signalton über die Web-Audio-Schnittstelle, ohne zusätzliche Datei. */
function playChime(): void {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor === undefined) {
      return;
    }
    const context = new Ctor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.2, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 1.2);
    oscillator.start();
    oscillator.stop(context.currentTime + 1.2);
    window.setTimeout(() => void context.close(), 1500);
  } catch {
    // Ohne Ton ist der Timer trotzdem sichtbar.
  }
}
